import { parseSchoolTopicKey } from 'shared/schoolLearn/schoolTopicEngagement';
import { logActivity } from 'shared/lib/activityLog';

const LESSON_STARTED_PREFIX = 'takshify.activity.lesson_started.';

export type LearnerLessonContext = {
  lessonId: string;
  lessonTitle?: string;
  schoolSlug?: string;
  batchId?: string;
  institutionId?: string;
  courseId?: string;
};

export type LearnerQuizContext = {
  scopeKey: string;
  examType: string;
  scorePct: number;
  passed: boolean;
  totalQuestions: number;
  scopeTitleEn?: string | null;
  quizId?: string;
  batchId?: string;
  institutionId?: string;
};

function parseTopicKey(topicKey: string): { schoolSlug: string; lessonId: string } {
  const parsed = parseSchoolTopicKey(topicKey);
  if (parsed) {
    return { schoolSlug: parsed.school, lessonId: parsed.topicId };
  }
  return { schoolSlug: 'parashari', lessonId: topicKey };
}

function shouldLogLessonStartedOnce(sessionKey: string): boolean {
  try {
    const storageKey = `${LESSON_STARTED_PREFIX}${sessionKey}`;
    if (sessionStorage.getItem(storageKey)) return false;
    sessionStorage.setItem(storageKey, '1');
    return true;
  } catch {
    return true;
  }
}

function baseLessonMetadata(ctx: LearnerLessonContext): Record<string, unknown> {
  return {
    lessonId: ctx.lessonId,
    lessonTitle: ctx.lessonTitle ?? null,
    schoolSlug: ctx.schoolSlug ?? null,
    batchId: ctx.batchId ?? null,
    courseId: ctx.courseId ?? null,
  };
}

/** Log lesson.started once per browser session per lesson key. */
export function logLessonStarted(ctx: LearnerLessonContext): void {
  const sessionKey = ctx.batchId
    ? `batch:${ctx.batchId}:${ctx.lessonId}`
    : `${ctx.schoolSlug ?? 'global'}:${ctx.lessonId}`;
  if (!shouldLogLessonStartedOnce(sessionKey)) return;

  logActivity({
    action: 'lesson.started',
    summary: ctx.lessonTitle ? `Started lesson: ${ctx.lessonTitle}` : 'Started a lesson',
    app: 'ai',
    institutionId: ctx.institutionId,
    resourceType: 'lesson',
    resourceId: ctx.lessonId,
    metadata: baseLessonMetadata(ctx),
  });
}

export function logLessonStartedFromTopicKey(topicKey: string, lessonTitle?: string): void {
  const { schoolSlug, lessonId } = parseTopicKey(topicKey);
  logLessonStarted({ lessonId, lessonTitle, schoolSlug });
}

/** Log lesson.completed when the learner marks a topic/lesson complete. */
export function logLessonCompleted(ctx: LearnerLessonContext): void {
  logActivity({
    action: 'lesson.completed',
    summary: ctx.lessonTitle ? `Completed lesson: ${ctx.lessonTitle}` : 'Completed a lesson',
    app: 'ai',
    institutionId: ctx.institutionId,
    resourceType: 'lesson',
    resourceId: ctx.lessonId,
    metadata: baseLessonMetadata(ctx),
  });
}

export function logLessonCompletedFromTopicKey(topicKey: string, lessonTitle?: string): void {
  const { schoolSlug, lessonId } = parseTopicKey(topicKey);
  logLessonCompleted({ lessonId, lessonTitle, schoolSlug });
}

/** Log quiz.submitted after a practice/daily/assess attempt is persisted. */
export function logQuizSubmitted(ctx: LearnerQuizContext): void {
  const title = ctx.scopeTitleEn?.trim();
  logActivity({
    action: 'quiz.submitted',
    summary: title
      ? `Submitted quiz: ${title} (${ctx.scorePct}%)`
      : `Submitted quiz (${ctx.scorePct}%)`,
    app: 'ai',
    institutionId: ctx.institutionId,
    resourceType: ctx.quizId ? 'quiz' : 'quiz_scope',
    resourceId: ctx.quizId ?? ctx.scopeKey,
    metadata: {
      scopeKey: ctx.scopeKey,
      examType: ctx.examType,
      scorePct: ctx.scorePct,
      passed: ctx.passed,
      totalQuestions: ctx.totalQuestions,
      batchId: ctx.batchId ?? null,
    },
  });
}

/** Log auth.sign_in after a fresh authentication (not tab refresh / token refresh). */
export function logAuthSignIn(method?: string): void {
  logActivity({
    action: 'auth.sign_in',
    summary: 'Signed in to Takshify',
    app: 'ai',
    metadata: { method: method ?? 'supabase' },
    severity: 'security',
  });
}

const SETTINGS_FIELD_LABELS: Record<string, string> = {
  displayName: 'display name',
  avatarUrl: 'avatar',
  preferredHomePage: 'home page',
  primarySchoolSlug: 'primary school',
  themePreference: 'theme',
  languagePreference: 'language',
  location: 'location',
  onboardingCompletedAt: 'onboarding',
};

/** Log settings.updated when learner profile preferences change (field keys only, no values). */
export function logSettingsUpdated(fields: string[]): void {
  const keys = fields.filter((field) => field in SETTINGS_FIELD_LABELS);
  if (!keys.length) return;

  const labels = keys.map((field) => SETTINGS_FIELD_LABELS[field] ?? field);
  const summary =
    labels.length === 1
      ? `Updated ${labels[0]} settings`
      : `Updated settings: ${labels.join(', ')}`;

  logActivity({
    action: 'settings.updated',
    summary,
    app: 'ai',
    resourceType: 'settings',
    metadata: { fields: keys },
    severity: 'info',
  });
}

/** Log data.export_requested when sadhak exports their activity (GDPR / DPDP portability). */
export function logDataExportRequested(input: {
  scope: string;
  format: string;
  rowCount: number;
}): void {
  logActivity({
    action: 'data.export_requested',
    summary: `Exported ${input.scope} (${input.format})`,
    app: 'ai',
    resourceType: 'export',
    metadata: {
      scope: input.scope,
      format: input.format,
      rowCount: input.rowCount,
    },
    severity: 'security',
  });
}

/** Log community.post_created for new threads or replies (no message body). */
export function logCommunityPostCreated(input: {
  postType: 'thread' | 'message';
  resourceId: string;
  channelId: string;
  threadId: string;
  channelName?: string | null;
  institutionId?: string | null;
  batchId?: string | null;
}): void {
  const summary =
    input.postType === 'thread'
      ? input.channelName
        ? `Started thread in #${input.channelName}`
        : 'Started a community thread'
      : input.channelName
        ? `Replied in #${input.channelName}`
        : 'Posted a community reply';

  logActivity({
    action: 'community.post_created',
    summary,
    app: 'ai',
    institutionId: input.institutionId ?? undefined,
    resourceType: input.postType === 'thread' ? 'thread' : 'message',
    resourceId: input.resourceId,
    metadata: {
      postType: input.postType,
      channelId: input.channelId,
      threadId: input.threadId,
      channelName: input.channelName ?? null,
      batchId: input.batchId ?? null,
    },
  });
}
